import { buildInvoiceTaxBreakdown } from '../../utils/taxBreakdown';
import {
  computeTotalsFromSO,
  fromCents,
  isCustomerFacingPriceRow,
  normalizeProductTaxIds,
} from '../../utils/totals';

// The generated document must explain the saved Sales Order taxes, not
// recalculate its financial stamp from the order-level default tax.
const asArray = (value) => Array.isArray(value) ? value : [];
const taxIdOf = (row = {}) => row?.taxId ?? row?.TaxId ?? row?.id ?? null;
const rateOf = (row = {}) => Number(row?.rate ?? row?.Rate ?? row?.value ?? 0);

const explicitlyUntaxed = (row = {}) => (
  row?.taxable === false ||
  row?.isTaxable === false ||
  row?.taxDetails?.taxable === false ||
  ['exempt', 'non-taxable', 'no-tax', 'none'].includes(
    String(row?.taxTreatment ?? row?.TaxTreatment ?? '').trim().toLowerCase()
  ) ||
  [row?.taxId, row?.TaxId].some((id) =>
    id !== null && id !== undefined && id !== '' && Number(id) === 0
  )
);

const productTaxRows = (product = {}, taxCatalog = []) => {
  // An explicit exemption is never replaced by a legacy saved tax or default.
  if (explicitlyUntaxed(product)) return [];

  // A saved line-level financial breakdown is preferred over a generic rate.
  const saved = [product?.taxBreakdown, product?.totals?.taxBreakdown]
    .find((rows) => Array.isArray(rows) && rows.length);
  if (saved) return saved;

  const details = [
    ...asArray(product?.taxDetailsList),
    ...asArray(product?.taxes),
    ...(product?.taxDetails && typeof product.taxDetails === 'object' ? [product.taxDetails] : []),
    ...(product?.taxRate && typeof product.taxRate === 'object' ? [product.taxRate] : []),
  ];
  const chosenIds = normalizeProductTaxIds({
    ...product,
    taxIds: asArray(product?.taxIds).length ? product.taxIds : product?.TaxIds,
  });
  if (chosenIds.length) {
    // Tax IDs select catalogue records, not percentages (which need not be unique).
    return chosenIds.map((id) =>
      [...details, ...taxCatalog].find((row) =>
        String(taxIdOf(row)) === String(id) && rateOf(row) > 0
      )
    ).filter(Boolean);
  }

  // Older product documents can have a taxRate object without an ID.
  return [product?.taxDetailsList, product?.taxes]
    .find((rows) => Array.isArray(rows) && rows.length) ||
    (product?.taxDetails && typeof product.taxDetails === 'object' ? [product.taxDetails] : null) ||
    (product?.taxRate && typeof product.taxRate === 'object' ? [product.taxRate] : null) ||
    [];
};

const savedOtherServices = (order = {}) => {
  const snapshot = order?.cartSnapshot || order?.stampedBookedAmount?.cartSnapshot || {};
  const payload = snapshot?.payload || {};
  // An explicitly empty active snapshot must not resurrect older DB services.
  return [
    payload?.OtherService, payload?.otherServices,
    snapshot?.OtherService, snapshot?.otherServices,
    order?.OtherService, order?.otherServices, order?.otherService,
  ].find(Array.isArray) || [];
};

const resolvedOtherService = (service = {}, taxCatalog = []) => {
  const chosenId = service?.TaxId ?? service?.taxId ?? null;
  const hasChosenId = chosenId != null && chosenId !== '' && Number(chosenId) > 0;
  const zeroPercent = service?.Taxes != null &&
    service.Taxes !== '' && Number(service.Taxes) === 0;

  if (explicitlyUntaxed(service) || (zeroPercent && !hasChosenId)) {
    return {
      ...service, TaxId: null, taxId: null, TaxIds: [], taxIds: [],
      Taxes: 0, Tax: null, tax: 0, taxRate: 0,
      taxDetails: null, taxDetailsList: [],
    };
  }

  // Source snapshots may carry an old tax detail for a different tax ID.
  // In that case the deliberate selection takes precedence over stale aliases.
  const rawDetails = asArray(service?.taxDetailsList);
  const selectedDetails = hasChosenId
    ? rawDetails.filter((detail) =>
      taxIdOf(detail) == null || String(taxIdOf(detail)) === String(chosenId)
    )
    : rawDetails;
  const matchingCatalogTax = hasChosenId
    ? taxCatalog.find((row) => String(taxIdOf(row)) === String(chosenId))
    : null;
  const detail = service?.taxDetails;
  const chosenDetail = detail && typeof detail === 'object' &&
    (!hasChosenId || taxIdOf(detail) == null || String(taxIdOf(detail)) === String(chosenId))
      ? detail : null;

  // OtherServices.Taxes is a percentage: 1 means 1%, not 100%.
  // A legacy Shipping.Tax value can instead be a dollar amount, never a rate.
  const percent = Number(service?.Taxes);
  const hasPercent = service?.Taxes != null && service.Taxes !== '' &&
    Number.isFinite(percent) && percent > 0;
  const savedDecimalRate = Number(service?.taxRate);
  const rate = hasPercent ? percent / 100
    : Number.isFinite(savedDecimalRate) && savedDecimalRate > 0 ? savedDecimalRate : 0;
  const genericDetail = !selectedDetails.length && !chosenDetail && !matchingCatalogTax && rate > 0
    ? [{ taxId: hasChosenId ? chosenId : null, rate, type: 'Tax' }]
    : [];

  return {
    ...service,
    // Never interpret the legacy uppercase monetary Tax field as a tax rate.
    ...(String(service?.Type ?? service?.type ?? '').toLowerCase() === 'shipping' && !hasPercent
      ? { Taxes: 0 } : {}),
    taxDetails: chosenDetail,
    taxDetailsList: selectedDetails.length ? selectedDetails : genericDetail,
  };
};

export const labelSalesOrderFooterTaxRow = (row = {}) => {
  const label = String(row?.label || 'Tax').trim() || 'Tax';
  const rate = Number(row?.rate);
  const suffix = row?.rate != null && Number.isFinite(rate) && rate > 0
    ? ` (${(rate * 100).toFixed(2)}%)`
    : '';
  return label.toLowerCase() === 'tax'
    ? `TAX${suffix}`
    : `TAX - ${label.toUpperCase()}${suffix}`;
};

const neutralTaxRow = (taxCents) => ({
  key: 'unclassified',
  label: 'Tax',
  rate: null,
  taxableSubtotalCents: 0,
  taxCents,
  sourceTaxIds: [],
});

export const buildSalesOrderFooterTaxBreakdown = (order = {}, totalsCents = null) => {
  const products = asArray(order?.products).filter((item) =>
    item?.isDeleted !== true && item?.isDeleted !== 'true'
  );
  const originalServices = savedOtherServices(order).filter((service) =>
    service?.isDeleted !== true &&
    service?.isDeleted !== 'true' &&
    isCustomerFacingPriceRow(service)
  );
  const taxCatalog = [
    ...asArray(order?.taxOptions),
    ...asArray(order?.taxes),
    ...(order?.tax && typeof order.tax === 'object' ? [order.tax] : []),
  ];

  // Reuse the invoice's established grouping logic with the saved SO amounts.
  // Its defaultTax is intentionally not a tax selection for an existing line.
  const items = products.map((product) => {
    const subtotal = fromCents(computeTotalsFromSO({ products: [product] }).subtotalCents);
    return {
      pricings: [{ Quantity: 1, Retail: subtotal, Total: subtotal }],
      taxDetailsList: productTaxRows(product, taxCatalog),
    };
  });
  const catalogWithProducts = [
    ...taxCatalog,
    ...items.flatMap((item) => item.taxDetailsList),
    ...originalServices.flatMap((service) => asArray(service?.taxDetailsList)),
  ];
  const otherServices = originalServices.map((service) =>
    resolvedOtherService(service, catalogWithProducts)
  );
  const allTaxes = [
    ...catalogWithProducts,
    ...otherServices.flatMap((service) => asArray(service?.taxDetailsList)),
  ];
  const rows = buildInvoiceTaxBreakdown({ items, otherServices, allTaxes });

  // The approved total is authoritative. Unidentifiable legacy tax must not
  // silently be called GST/HST, nor disappear beside identifiable tax rows.
  const targetTaxCents = Number(totalsCents?.taxCents);
  if (totalsCents == null || !Number.isFinite(targetTaxCents)) return rows;
  const difference = targetTaxCents - rows.reduce((sum, row) => sum + row.taxCents, 0);
  if (difference === 0) return rows;
  const hasUnknownProductTax = products.some((product, i) =>
    Number(product?.taxAmount ?? product?.TaxAmount ?? 0) > 0 &&
    !items[i].taxDetailsList.some((detail) => rateOf(detail) > 0)
  );
  if (!hasUnknownProductTax && rows.length && Math.abs(difference) <= 5) {
    const largestIndex = rows.reduce(
      (best, row, i) => row.taxCents > rows[best].taxCents ? i : best, 0
    );
    return rows.map((row, i) => i === largestIndex
      ? { ...row, taxCents: row.taxCents + difference } : row);
  }
  if (difference > 0) return [...rows, neutralTaxRow(difference)];
  // Conflicting historical metadata: keep the saved tax total, not a
  // misleading set of tax labels that add to more than the recorded tax.
  return targetTaxCents > 0 ? [neutralTaxRow(targetTaxCents)] : [];
};
